import { css, type CSSObject } from '@emotion/react'
import { TypeOptions } from 'react-toastify'
import {
  Shadow,
  ShadowElevation,
  Size,
  Spacing,
  Typography,
  FontWeight,
  gemVar,
  ThemedColors,
} from '@opallabs/gem'

const insetBuffer = Spacing.XS * 15
const toastConstants = {
  toastWidth: Spacing.M * 24.5,
  toastHeight: Spacing.M * 4.75,
  typeIconSectionWidth: Spacing.XS * 5,
  closeIconSectionWidth: Spacing.XL,
} as const

export const reactToastifyOverrides = css({
  borderRadius: Size.Rounding,
  padding: 0,
  inlineSize: `${toastConstants.toastWidth}px !important`,

  // Adjust for floating headers
  '&.Toastify__toast-container--top-right': {
    insetBlockStart: `calc(${insetBuffer}px + 1em)`,
  },
  '&.Toastify__toast-container--top-left': {
    insetBlockStart: `calc(${insetBuffer}px + 1em)`,
  },
  '&.Toastify__toast-container--top-center': {
    insetBlockStart: `calc(${insetBuffer}px + 1em)`,
  },

  '&.Toastify__toast-container--bottom-right': {
    insetBlockEnd: `calc(${insetBuffer}px + 1em)`,
  },
  '&.Toastify__toast-container--bottom-left': {
    insetBlockEnd: `calc(${insetBuffer}px + 1em)`,
  },
  '&.Toastify__toast-container--bottom-center': {
    insetBlockEnd: `calc(${insetBuffer}px + 1em)`,
  },
  '.Toastify__toast': {
    borderRadius: Size.Rounding * 3,
    background: 'none',
    padding: 0,
  },
  '.Toastify__toast-body': {
    borderRadius: Size.Rounding * 3,
    padding: 0,
    inlineSize: '100%',
  },
})

const boxReset: CSSObject = {
  boxSizing: 'border-box',

  '*': { boxSizing: 'border-box' },
}

const flexCenter: CSSObject = {
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'center',
}

const flexFreeze: CSSObject = {
  flexShrink: 0,
  flexGrow: 0,
}

const toastBase: CSSObject = {
  borderRadius: Size.Rounding * 3,
  ...Typography.T500,
  minBlockSize: toastConstants.toastHeight,
  position: 'relative',
  // Ensure toasts that appear above Radix drawers are interactive
  pointerEvents: 'auto',
}

export const baseToast = css({
  ...boxReset,
  ...toastBase,
  alignItems: 'stretch',
  backgroundColor: gemVar(ThemedColors.Surface1),
  color: gemVar(ThemedColors.TextPrimary),
  display: 'flex',
  flexWrap: 'nowrap',
  justifyContent: 'space-between',
  ...Shadow[ShadowElevation.E2],
})

export const baseActionToast = css({
  ...boxReset,
  ...toastBase,
  backgroundColor: gemVar(ThemedColors.Surface1),
  color: gemVar(ThemedColors.TextPrimary),
  ...Shadow[ShadowElevation.E2],
})

export const actionWrapper = css({
  alignItems: 'stretch',
  display: 'flex',
  flexWrap: 'nowrap',
  justifyContent: 'space-between',
})

export const typeIconContainer = ({
  type,
}: {
  type: TypeOptions | undefined
}) =>
  css({
    ...flexCenter,
    ...flexFreeze,
    borderStartStartRadius: Size.Rounding,
    borderEndStartRadius: Size.Rounding,
    borderRadius: type === 'success' ? '100%' : 0,
    blockSize: toastConstants.typeIconSectionWidth,
    marginBlockStart: Spacing.M,
    marginInlineEnd: Spacing.XS * 3,
    marginInlineStart: Spacing.M,
    inlineSize: toastConstants.typeIconSectionWidth,
    backgroundColor:
      type === 'success' ? gemVar(ThemedColors.Success1) : 'transparent',
  })

export const toastBody = css({
  alignItems: 'center',
  display: 'flex',
  blockSize: '100%',
  justifyContent: 'space-between',
  flexGrow: 1,
})

export const errorCount = css({
  backgroundColor: gemVar(ThemedColors.Error1),
  color: gemVar(ThemedColors.TextContrast),
  ...Typography.T300,
  borderRadius: Size.Rounding,
  blockSize: Spacing.M * 1.25,
  inlineSize: Spacing.M * 1.25,
  textAlign: 'center',
  display: 'inline-flex',
  justifyContent: 'center',
  alignItems: 'center',
})

export const messageContainer = css({
  lineHeight: '20px',
  paddingBlockStart: Spacing.M,
  paddingBlockEnd: Spacing.S,
})

export const messageTitle = ({ type }: { type?: TypeOptions }) =>
  css({
    ...Typography.H100,
    fontWeight: FontWeight.Bold,
    lineHeight: '20px',
    color:
      type === 'success'
        ? gemVar(ThemedColors.Success1)
        : gemVar(ThemedColors.TextPrimary),
  })

export const message = css({
  color: gemVar(ThemedColors.TextSecondary),
  marginBlock: Spacing.XS,
})

export const action = css({
  marginInlineStart: Spacing.XL,
  paddingBlockEnd: Spacing.M,
})

export const closeIconWrapper = css({
  display: 'flex',
  flexDirection: 'column',
})

export const closeIcon = css({
  ...flexFreeze,
  alignContent: 'flex-end',
  marginBlockStart: Spacing.S,
  marginInlineEnd: Spacing.S,
  blockSize: 24,
  color: gemVar(ThemedColors.TextPrimary),

  '&.icon-only': {
    paddingInline: Spacing.XS,
  },
})
